import * as React from 'react';
import { ASK_RECOVER } from '../../state/mutations/User';
import {
  KeyboardAvoidingView,
  Modal,
  StyleSheet,
  View,
} from 'react-native';
import { URL } from '../../config/config';
import { isWeb } from '../../config/designSystem/Layout';
import { observer } from 'mobx-react';
import { useMutation} from '@apollo/client';
import { useStores } from '../../hooks/useStores';

import Button from '../../components/Button/Button';
import { ERROR_COLOR, NEUTRAL_TEXT0 } from '../../config/designSystem/Colors';
import { H3, ParagraphMRegular, ParagraphSRegular } from '../../config/designSystem/typography';
import { InputContainer, SafeView } from '../../config/designSystem/styledComponents';
import { SPACING } from '../../config/designSystem/Spacing';
import { TextInput2 } from '../../components/TextInput/TextInput2';

import { TwoButtonsVertical } from '../../components/Button/TwoButtonsVertical';
import { concatPath } from '../../utils/links/concatPath';
import { loginKey } from '../../constants/routeKeys';
import { router } from 'expo-router';
import { useVerticalOffsetHeight } from '../../hooks/ui/useVerticalOffsetHeight';

interface ForgotPasswordProps {}


const ForgotPassword: React.FunctionComponent<ForgotPasswordProps> = observer(() => {
  const rootStore = useStores();

  const [modalVisible, setModalVisible] = React.useState(false);
  const [forgotStatus, setForgotStatus] = React.useState('unset');
  const emailInputRef = React.useRef<any>(null);
  const language = rootStore?.language?.name?.key ? rootStore?.language?.name?.key : 'fr';
  const labels = rootStore?.language.translate.MOBILE?.FORGOT_PASSWORD;
  const [email, setEmail] = React.useState('');
  const verticalOffSetHeight = useVerticalOffsetHeight();


  const [forgotPasswordMutation] = useMutation(
    ASK_RECOVER, {
      fetchPolicy:'no-cache',
      onCompleted: () => {
        setModalVisible(true);
      },
      onError: err => {
        setModalVisible(true);
      }
    }
  );

  const goToLogin = () => {
    router.navigate(concatPath([ loginKey], 0) as string);
  };



  const onPressForgot = () => {

    if (!email.includes('@') || email === '') {
      setForgotStatus('notCompletedField');
    } else {
      const linkUrl = `${URL}/${language}/user/auth/reset-password/${email.toLowerCase().trim()}`;
      forgotPasswordMutation({
        variables: {
          locale: language,
          email: email.toLowerCase().trim(),
          link: linkUrl,
        },
      });
    }
  };


  return (
    <SafeView >
      <Modal animationType='fade' transparent={true} visible={modalVisible}>
        <View style={styles.centeredView}>
          <View style={styles.modalView}>

            <ParagraphMRegular>
              {labels?.CHECK_EMAIL
                ? labels?.CHECK_EMAIL
                : 'Please check your email for the activation link'}
            </ParagraphMRegular>


            <Button  onPress={() => {
              setModalVisible(!modalVisible);
              goToLogin();
            }} label={labels?.MODAL_BACK_BUTTON
              ? labels?.MODAL_BACK_BUTTON
              : 'Take me to Login'} paddingHorizontal={20} paddingVertical={20} color={NEUTRAL_TEXT0}/>
          </View>
        </View>
      </Modal>
      <KeyboardAvoidingView behavior='padding'
        keyboardVerticalOffset={verticalOffSetHeight}
        // flex:1 matters: without it the view sizes to its content instead of the
        // screen, so it never shrinks when the keyboard opens and the buttons below
        // stayed underneath it. It also makes the buttons' own flex:1 wrapper work.
        style={{ flex:1, alignItems:'center', justifyContent:'flex-start'}}>


        <View style={[{ alignItems:'center', justifyContent:'center'}]}>
          <H3>{labels.GET_A_NEW_PASSWORD}</H3>
          <InputContainer style={{justifyContent:'center', maxWidth:'80%'}}>
            <TextInput2
              reference={emailInputRef}
              label={
                labels?.EMAIL_PLACEHOLDER
                  ? labels?.EMAIL_PLACEHOLDER
                  : 'Email'
              }
              value={email}
              onChangeText={(text: string) => setEmail(text)}
              colorFrame='grey'
              returnKeyType='done'
              textContentType='emailAddress'
              image='envelope'
              secured={false}
              autoFocus={true}
            />
          </InputContainer>
          {forgotStatus === 'notCompletedField' && (

            <ParagraphSRegular color={ERROR_COLOR}>
              {labels?.ERROR_COMPLETE_FIELDS
                ? labels?.ERROR_COMPLETE_FIELDS
                : 'Please complete the fields above.'}
            </ParagraphSRegular>
          )}
        </View>
        {/* No flex:1 on native. It pushed these buttons to the bottom of the screen,
            which on this always-autoFocused screen is behind the keyboard, leaving
            them unreachable. Flowing them under the field keeps them visible. */}
        <View style={[{  alignSelf:'center', justifyContent:'flex-end'},
          isWeb ? {flex:1, marginBottom: 20} : {marginTop: SPACING['sp-32']}]} >
          <TwoButtonsVertical
            onPressTop={onPressForgot}
            onPressBelow={() => {

              if(router.canGoBack()) {
                router.back();
              } else {
                goToLogin();
              }
            }}
            labelTop={labels?.SEND_BUTTON
              ? labels?.SEND_BUTTON
              : 'Send a reset link'}
            labelBelow={labels?.BACK_BUTTON
              ? labels?.BACK_BUTTON
              : 'Go back to login'}
          />




        </View>


      </KeyboardAvoidingView>
    </SafeView>
  );
});

const styles = StyleSheet.create({
  centeredView: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.5)',

  },
  modalView: {
    margin: 20,
    backgroundColor: 'white',
    borderRadius: 10,
    padding: 35,
    alignItems: 'center',
    rowGap:20,
    elevation: 5,
  },
  modalText: {
    marginBottom: 15,
    textAlign: 'center',
  },
});

export default ForgotPassword;

